import {useCallback, useEffect, useState} from 'react';

import {AmountInput, Button, Modal, SpinningLoader} from '@/components';
import type {
  AfriexAdminAmount,
  AfriexCountry,
  AfriexReferralConfig,
} from '@/types';
import {AfriexCurrencyCode} from '@/types';

type FormProps = {
  onClose: VoidFunction;
  showModal: boolean;
  handleSave: (id: string, body: Partial<AfriexCountry>) => Promise<void>;
  item: AfriexCountry;
};

const ReferralSettingsModal = ({
  onClose,
  handleSave,
  item,
  showModal,
}: FormProps) => {
  const [formValues, setFormValues] = useState<Partial<AfriexReferralConfig>>(
    {} as AfriexReferralConfig,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (item?.params?.referralParams) {
      setFormValues(item?.params?.referralParams);
    }
  }, [item?.params?.referralParams]);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      const updateData = {
        params: {...item.params, referralParams: formValues},
      } as Partial<AfriexCountry>;
      setIsLoading(true);
      try {
        await handleSave(String(item?.id), updateData);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    },
    // `item` rather than its `id`/`params` leaves: the body reads both off the
    // same object, so `item` is the honest dependency. `handleSubmit` is only
    // handed to a plain <form onSubmit>, so a fresher identity costs nothing.
    [formValues, handleSave, item],
  );

  return (
    <Modal
      isOpen={showModal}
      onClose={onClose}
      width="w-11/12 md:w-5/12 lg:w-4/12 xl:w-3/12 px-4 sm:px-0"
      title={`${item.name} Referral Settings`}
      closeOnOutsideClick
    >
      <div className="mx-5">
        {isLoading ? (
          <div className="flex w-full h-full justify-center items-center my-5">
            <SpinningLoader />
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="flex flex-col space-y-5">
              <div className="mt-5">
                <AmountInput
                  value={{
                    amount: formValues?.cumulativeThreshold || 0,
                    currency: AfriexCurrencyCode.USD,
                  }}
                  label="Referral Transaction Threshold"
                  onChange={(e: AfriexAdminAmount) =>
                    setFormValues((x) => ({
                      ...x,
                      cumulativeThreshold: e?.amount || 0,
                    }))
                  }
                />
              </div>

              <div className="mt-5">
                <AmountInput
                  value={{
                    amount: formValues?.bonusAmountPerReferrer || 0,
                    currency: AfriexCurrencyCode.USD,
                  }}
                  fixedCurrency={AfriexCurrencyCode.USD}
                  label="Bonus Amount Per Referrer (in USD)"
                  onChange={(e: AfriexAdminAmount) =>
                    setFormValues((x) => ({
                      ...x,
                      bonusAmountPerReferrer: e?.amount || 0,
                    }))
                  }
                />
              </div>

              <div className="mt-5">
                <AmountInput
                  fixedCurrency={AfriexCurrencyCode.USD}
                  value={{
                    amount: formValues?.bonusAmountPerReferee || 0,
                    currency: AfriexCurrencyCode.USD,
                  }}
                  label="Bonus Amount Per Referree (in USD)"
                  onChange={(e: AfriexAdminAmount) =>
                    setFormValues((x) => ({
                      ...x,
                      bonusAmountPerReferee: e?.amount || 0,
                    }))
                  }
                />
              </div>

              <div className="flex w-full justify-end space-x-2 py-5">
                <Button onClick={onClose} variant="outline">
                  Cancel
                </Button>
                <Button disabled={isLoading} type="submit" variant="solid">
                  Submit
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ReferralSettingsModal;
